import React, { useState } from 'react';
import { Search, Loader2, Car, ListPlus, Play } from 'lucide-react';
import api from '../lib/api';
import { toast } from '../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const TYPES = [['delivery', 'Delivery'], ['entrada', 'Detail Entrada']];

export const DetailWorkDialog = ({ open, onClose, onCreated }) => {
  const [vin, setVin] = useState('');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [car, setCar] = useState(null);
  const [tipo, setTipo] = useState('delivery');
  const [cor, setCor] = useState('');
  const [notas, setNotas] = useState('');

  const reset = () => { setVin(''); setCar(null); setTipo('delivery'); setCor(''); setNotas(''); };

  const buscar = async () => {
    const v = vin.trim().toUpperCase();
    if (v.length < 11) return toast({ title: 'VIN inválido', description: 'Digite o VIN completo (17 caracteres).' });
    setBusy(true); setCar(null);
    try {
      const { data } = await api.get(`/detail/vin/${v}`);
      setCar(data);
    } catch (e) {
      toast({ title: 'VIN não encontrado', description: e.response?.data?.detail || 'Confira o VIN digitado.' });
    } finally { setBusy(false); }
  };

  const criar = async (start_now) => {
    setSaving(true);
    try {
      const { data } = await api.post('/detail/jobs', { ...car, cor, tipo, notas, start_now });
      toast({ title: start_now ? '▶ Trabalho iniciado' : '✓ Adicionado à fila', description: `${data.ano} ${data.marca} ${data.modelo}` });
      reset(); onClose(); onCreated(data);
    } catch (e) {
      toast({ title: 'Erro', description: e.response?.data?.detail || '' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent aria-describedby={undefined} className="max-w-xl border-border bg-card text-foreground" style={{ borderRadius: 3 }}>
        <DialogHeader>
          <DialogTitle className="font-display text-lg font-black uppercase tracking-tight">Adicionar trabalho</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <p className="ent-label mb-1.5">VIN do veículo</p>
            <div className="flex gap-2">
              <input data-testid="detail-vin-input" value={vin} maxLength={17} autoFocus
                onChange={(e) => setVin(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && buscar()}
                placeholder="1HGCM82633A004352"
                className="ent-input dt-timer flex-1 uppercase tracking-[0.12em]" />
              <button data-testid="detail-vin-search" onClick={buscar} disabled={busy} className="ent-btn ent-btn-primary h-8 px-4">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />} Buscar
              </button>
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground/70">Identificação automática de ano, marca e modelo pela base NHTSA.</p>
          </div>

          {car && (
            <div data-testid="detail-vin-result" className="border border-primary/40 bg-primary/[0.04] p-4" style={{ borderRadius: 3 }}>
              <div className="flex items-start gap-3">
                <Car className="mt-0.5 h-5 w-5 text-primary" />
                <div className="min-w-0">
                  <p className="font-display text-xl font-black uppercase leading-tight">{car.ano} {car.marca} {car.modelo}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{[car.trim, car.carroceria].filter(Boolean).join(' · ') || '—'}</p>
                  <p className="dt-timer mt-1 text-[11px] tracking-wider text-muted-foreground/70">{car.vin}</p>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                {TYPES.map(([k, l]) => (
                  <button key={k} data-testid={`detail-type-${k}`} onClick={() => setTipo(k)}
                    className={`border px-3 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${tipo === k ? 'border-primary bg-primary/12 text-foreground' : 'border-border text-muted-foreground hover:text-foreground'}`}
                    style={{ borderRadius: 3 }}>{l}</button>
                ))}
              </div>

              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <input data-testid="detail-color-input" value={cor} onChange={(e) => setCor(e.target.value)} placeholder="Cor (opcional)" className="ent-input" />
                <input data-testid="detail-notes-input" value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Observações (opcional)" className="ent-input" />
              </div>

              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <button data-testid="detail-add-queue" disabled={saving} onClick={() => criar(false)} className="ent-btn h-9 flex-1 justify-center">
                  <ListPlus className="h-3.5 w-3.5" /> Jogar para fila
                </button>
                <button data-testid="detail-start-now" disabled={saving} onClick={() => criar(true)} className="ent-btn ent-btn-primary h-9 flex-1 justify-center">
                  <Play className="h-3.5 w-3.5" /> Fazer agora
                </button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
